(() => {
  if (window.pogiHeroVideoInitialized) return;
  window.pogiHeroVideoInitialized = true;

  const controllers = new Map();
  const videoOwners = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;

  function initialize(root = document) {
    const heroes = [...root.querySelectorAll('.pogi-hhero--video')];
    if (root.matches?.('.pogi-hhero--video')) heroes.unshift(root);
    heroes.forEach(hero => {
      if (controllers.has(hero)) return;
      const video = hero.querySelector('video');
      const toggle = hero.querySelector('[data-hero-video-toggle]');
      if (!video || !toggle) return;
      const owner = {};
      videoOwners.set(video, owner);

      let intent = 'auto';
      let inView = false;
      let disposed = false;
      let autoplayBlocked = false;
      let pendingPlay = null;
      let visibilityFrame = 0;
      let observer;
      const cleanup = [];
      const listen = (target, type, handler, options) => {
        target.addEventListener(type, handler, options);
        cleanup.push(() => target.removeEventListener(type, handler, options));
      };
      const mayPlay = () => !disposed && !document.hidden && inView && intent !== 'pause'
        && !autoplayBlocked && (intent === 'play' || (!reducedMotion.matches && !connection?.saveData));
      const isPlaying = () => !video.paused || Boolean(pendingPlay && mayPlay());
      const sync = () => {
        const playing = isPlaying();
        toggle.setAttribute('aria-label', playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
        toggle.toggleAttribute('data-playing', playing);
        if (pendingPlay && mayPlay()) toggle.setAttribute('aria-busy', 'true');
        else toggle.removeAttribute('aria-busy');
      };
      const pause = () => {
        if (pendingPlay) pendingPlay.cancelled = true;
        video.pause();
        sync();
      };
      // Choose one resolution before the first load, then let the browser choose
      // AV1 or MP4. Consuming the deferred URLs freezes the choice across resizes.
      const hydrateSources = () => {
        const mediaSources = [video, ...video.querySelectorAll('source[data-src]')]
          .filter(media => media.dataset.src);
        if (!mediaSources.length) return;
        const desktop = window.matchMedia('(min-width: 991px)').matches;
        for (const media of mediaSources) {
          const desktopSource = desktop && media.dataset.srcDesktop;
          if (desktopSource && media.dataset.typeDesktop) {
            media.setAttribute('type', media.dataset.typeDesktop);
          }
          media.setAttribute('src', desktopSource || media.dataset.src);
          delete media.dataset.src;
          delete media.dataset.srcDesktop;
          delete media.dataset.typeDesktop;
        }
        video.load();
      };
      const reconcile = () => {
        if (!mayPlay()) {
          pause();
          return;
        }
        if (!video.paused || pendingPlay) {
          sync();
          return;
        }
        hydrateSources();
        const attempt = { cancelled: false };
        pendingPlay = attempt;
        sync();
        const settled = (failed) => {
          // A section reload can reuse this video before the old promise settles.
          if (videoOwners.get(video) !== owner) return;
          if (disposed) {
            video.pause();
            return;
          }
          if (pendingPlay !== attempt) return;
          pendingPlay = null;
          // A visibility pause can abort play(); that is not an autoplay denial.
          if (failed && !attempt.cancelled) autoplayBlocked = true;
          if (!mayPlay()) pause();
          else if (attempt.cancelled) reconcile();
          sync();
        };
        try {
          Promise.resolve(video.play()).then(() => settled(false), () => settled(true));
        } catch {
          settled(true);
        }
      };
      const updateVisibility = () => {
        const rect = video.getBoundingClientRect();
        const height = window.innerHeight || document.documentElement.clientHeight;
        const width = window.innerWidth || document.documentElement.clientWidth;
        inView = rect.width > 0 && rect.height > 0
          && rect.bottom > 0 && rect.top < height && rect.right > 0 && rect.left < width;
        reconcile();
      };

      video.muted = true;
      toggle.hidden = false;
      listen(toggle, 'click', () => {
        // A cancelled promise can still be pending after the button returns to Play.
        if (isPlaying()) intent = 'pause';
        else {
          intent = 'play';
          autoplayBlocked = false;
        }
        reconcile();
      });
      listen(video, 'play', () => {
        if (!mayPlay()) pause();
        else sync();
      });
      listen(video, 'pause', sync);
      listen(video, 'error', () => {
        autoplayBlocked = true;
        pause();
      });
      listen(document, 'visibilitychange', reconcile);
      if (reducedMotion.addEventListener) listen(reducedMotion, 'change', reconcile);
      else if (reducedMotion.addListener) {
        reducedMotion.addListener(reconcile);
        cleanup.push(() => reducedMotion.removeListener(reconcile));
      }
      if (connection?.addEventListener) listen(connection, 'change', reconcile);

      if ('IntersectionObserver' in window) {
        observer = new window.IntersectionObserver(entries => {
          if (disposed) return;
          const entry = entries[entries.length - 1];
          inView = entry.isIntersecting && entry.intersectionRatio >= 0.01;
          reconcile();
        }, { threshold: 0.01 });
        observer.observe(video);
      } else {
        const queueVisibility = () => {
          if (visibilityFrame) return;
          visibilityFrame = window.requestAnimationFrame(() => {
            visibilityFrame = 0;
            updateVisibility();
          });
        };
        listen(document, 'scroll', queueVisibility, { capture: true, passive: true });
        listen(window, 'resize', queueVisibility, { passive: true });
        updateVisibility();
      }
      sync();
      controllers.set(hero, () => {
        disposed = true;
        observer?.disconnect();
        if (visibilityFrame) window.cancelAnimationFrame(visibilityFrame);
        cleanup.forEach(remove => remove());
        pause();
        toggle.hidden = true;
        controllers.delete(hero);
      });
    });
  }

  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [hero, dispose] of controllers) {
      if (event.target.contains(hero)) dispose();
    }
  });
})();
